import { inject, Injectable, OnDestroy } from '@angular/core';
import { IdentityServiceBase } from '@seahorse/auth';
import {
  EnvService,
  FileHelpers,
  NotificationService,
  SignalRMessage,
} from '@seahorse/common';
import { hubConnection } from 'signalr-no-jquery';
import { Subject, Subscription } from 'rxjs';
import { ExportReadyHubMessage } from '../models/content-distribution.model';
import { map, mergeMap } from 'rxjs/operators';
import { ExportReadyToastTemplate } from '../components/export-ready-toast/export-ready-toast.template';
import { ContentDistributionDataService } from './content-distribution-data.service';

// Minimal typings for classic ASP.NET SignalR client
interface LegacyStart {
  done(callback: () => void): LegacyStart;
  fail(callback: (err: unknown) => void): LegacyStart;
}

interface LegacyHubProxy {
  on(event: string, handler: (payload: string) => void): void;
}

interface LegacyHubConnection {
  logging: boolean;
  start(): LegacyStart;
  stop(): void;
  createHubProxy(name: string): LegacyHubProxy;
}

export class HubConstants {
  static CONTENT_DISTRIBUTION = {
    EXPORT_READY: 'EXPORT_READY',
    DISCONNECT: 'DISCONNECT',
  };
}

@Injectable({
  providedIn: 'root',
})
export class ContentDistributionHubService implements OnDestroy {
  private readonly _hubName = 'content-distribution';

  private _identityService = inject(IdentityServiceBase);
  private contentDistributionService = inject(ContentDistributionDataService);
  private _notificationService = inject(NotificationService);
  private _envService = inject(EnvService);
  private _connection: LegacyHubConnection | null = null;
  private _hubProxy: LegacyHubProxy | null = null;

  private _exportReadyEventsSubject = new Subject<ExportReadyHubMessage>();
  exportReadyEvents$ = this._exportReadyEventsSubject.asObservable();

  private _subscriptions = new Subscription();

  constructor() {
    this.setUpExportReadyListener();
  }

  init() {
    // Build the connection using ASP.NET (classic) SignalR client
    if (!this._connection) {
      // For classic SignalR, hubConnection expects the host root (it appends /signalr by default)
      this._connection = <LegacyHubConnection>(
        (<unknown>hubConnection(this._envService.apiHost))
      );
      this._connection.logging = true;

      // Create hub proxy using the server hub name
      this._hubProxy = this._connection.createHubProxy(this._hubName);

      // Register server-to-client handler
      this._hubProxy.on('ReceiveMessage', (rawMessage: string) => {
        try {
          const message = JSON.parse(
            rawMessage
          ) as SignalRMessage<ExportReadyHubMessage>;
          if (
            message.action === HubConstants.CONTENT_DISTRIBUTION.EXPORT_READY &&
            message.connectionId === this._identityService.identity.id
          ) {
            this._exportReadyEventsSubject.next(message.message);
          }
        } catch (e) {
          console.error('Failed to parse SignalR message', e);
        }
      });
    }

    // Start the connection
    this._connection
      .start()
      .done(() => {
        console.log('SignalR (ASP.NET) connection established');
      })
      .fail((err: unknown) => {
        console.error('Error establishing SignalR connection:', err);
      });
  }

  private setUpExportReadyListener() {
    this.exportReadyEvents$
      .pipe(
        mergeMap((message) => {
          return this._notificationService
            .show(ExportReadyToastTemplate, {
              filename: message.filename,
            })
            .download.pipe(map(() => message));
        }),
        mergeMap((message) =>
          this.contentDistributionService.downloadExprtedFile(message).pipe(
            map((response) => ({
              file: response,
              filename: message.filename,
            }))
          )
        )
      )
      .subscribe((response) => {
        FileHelpers.downloadBlob(response.file, response.filename);
      });
  }

  ngOnDestroy() {
    this._subscriptions?.unsubscribe();
    this._connection?.stop();
  }
}
